import * as React from "react";
import { forwardRef } from "react";
import styled, { css } from "styled-components";
const StyledItem = styled.li(
  ({ theme }) => css`
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.table.row.divider};
    }
  `
);
const InnerContainer = styled.div(
  ({ theme, $active, $disabled, $bsStyle, $isInteractive }) => css`
    border: none;
    font: inherit;
    text-align: left;
    width: 100%;
    padding: 5px 10px;
    background-color: ${theme.colors.global.contentBackground};
    line-height: 1.25;
    color: ${theme.colors.text.primary};
    text-decoration: none;

    .list-group-item-heading {
      font-size: ${theme.fonts.size.h5};
    }

    .list-group-item-text {
      margin-bottom: 5px;
    }

    ${$isInteractive && css`
      cursor: pointer;

      .list-group-item-heading {
        color: ${theme.colors.variant.darkest.default};
      }

      &:hover:not([disabled]),
      &:focus:not([disabled]) {
        color: inherit;
        background-color: ${theme.utils.colorLevel(theme.colors.global.contentBackground, 10)};

        .list-group-item-heading {
          color: ${theme.utils.readableColor(theme.colors.variant.lightest.default)};
        }
      }
    `}

    ${$disabled && css`
      pointer-events: none;
      color: ${theme.colors.text.disabled};
      background-color: ${theme.colors.variant.lightest.default};

      .list-group-item-heading {
        color: inherit;
      }

      .list-group-item-text {
        color: ${theme.colors.variant.default};
      }
    `}

    ${$active && css`
      color: ${theme.colors.variant.darker.default};
      background-color: ${theme.colors.variant.lightest.info};

      .list-group-item-heading,
      .list-group-item-heading > small,
      .list-group-item-heading > .small {
        color: inherit;
      }

      .list-group-item-text {
        color: ${theme.colors.variant.light.primary};
      }
    `}

    ${$bsStyle && css`
      color: ${theme.utils.readableColor(theme.colors.variant.lighter[$bsStyle])};
      background-color: ${theme.colors.variant.lighter[$bsStyle]};

      ${$isInteractive && css`
        &:hover,
        &:focus {
          color: ${theme.utils.readableColor(theme.colors.variant.lighter[$bsStyle])};
          background-color: ${theme.colors.variant.light[$bsStyle]};
        }

        ${$active && css`
          color: ${theme.utils.readableColor(theme.colors.variant.light[$bsStyle])};
          background-color: ${theme.colors.variant.light[$bsStyle]};
        `}
      `}
    `}
  `
);
const ItemBody = ({
  href = void 0,
  disabled = void 0,
  onClick = void 0,
  onKeyDown = void 0,
  children,
  ...styledProps
}) => {
  if (href) {
    return /* @__PURE__ */ React.createElement(InnerContainer, { as: "a", href, ...styledProps }, children);
  }
  if (onClick) {
    return /* @__PURE__ */ React.createElement(
      InnerContainer,
      {
        as: "button",
        type: "button",
        disabled,
        onClick,
        onKeyDown,
        ...styledProps
      },
      children
    );
  }
  return /* @__PURE__ */ React.createElement(InnerContainer, { ...styledProps }, children);
};
const ListGroupItem = ({
  active = void 0,
  bsStyle = void 0,
  children = void 0,
  className = void 0,
  disabled = void 0,
  header = void 0,
  href = void 0,
  id = void 0,
  onClick = void 0,
  onKeyDown = void 0
}, ref) => {
  const isInteractive = !!(onClick || href);
  const content = /* @__PURE__ */ React.createElement(React.Fragment, null, header && /* @__PURE__ */ React.createElement("div", { className: "list-group-item-heading" }, header), header ? /* @__PURE__ */ React.createElement("p", { className: "list-group-item-text" }, children) : children);
  return /* @__PURE__ */ React.createElement(StyledItem, { ref, id }, /* @__PURE__ */ React.createElement(
    ItemBody,
    {
      href,
      disabled,
      onClick,
      onKeyDown,
      className,
      $active: active,
      $disabled: disabled,
      $bsStyle: bsStyle,
      $isInteractive: isInteractive
    },
    content
  ));
};
export default forwardRef(ListGroupItem);
